import { LineString } from "ol/geom";
import { Stroke, Style } from "ol/style";
import { extractTags } from "./propertyExtraction";
import {
  bandGeometry,
  extendEnds,
  laneCount,
  laneSlots,
  offsetCoordinates,
} from "./util/laneBands.js";
import {
  ARROW_HIDE_RESOLUTION,
  buildArrowStyles,
  laneDirectionArrowSpecs,
} from "./util/laneDirectionArrows";
import { sampleAlong } from "./util/polyline.mjs";
import { splitConditionalValue } from "./util/conditionalValues.mjs";
import { styleBySection } from "./util/stepSectionStyle";
import {
  ROAD_CLASS_MINOR,
  ROAD_CLASS_SERVICE,
  Z_RESTRICTION,
  ROAD_CLASS_UNTAGGED,
  roadClass,
  roadClassStroke,
} from "./util/roadClass";

// The OSM-normalized lane outputs are all per-lane pipe lists in physical
// left-to-right order, so collapsing them to one colour per way would throw away
// what they say. They are drawn as bands INSIDE one centreline stroke — see
// util/laneBands.js for the cross-section maths and why the separators are slivers
// of a casing rather than strokes of their own.
//
// The two splits in play here are orthogonal and must stay so: styleBySection cuts
// the way LONGITUDINALLY at LR step boundaries, this file cuts each of those slices
// LATERALLY into lanes. Sections may carry different lane counts, so the width, band
// and offsets are recomputed per section rather than per feature.

// An open lane still has to be painted — in band rendering an unpainted lane is a
// missing lane, not an unremarkable one. Near-white so it reads as "nothing to
// report", and so it contrasts hard with the casing showing between the bands, which
// is what keeps the lane count countable at ~2px per band.
const OPEN_LANE_COLOR = "#e0e0e0";
// The casing under the bands. Its outer edge doubles as the stroke's outline against
// a light basemap, and the slivers it shows between bands are the separators. Dark,
// because the common case is open lanes side by side and those are light; but not
// pure black, which would leave no visible seam between two dark bands (maroon,
// purple).
const SEPARATOR_COLOR = "#333";
const CLOSED_LANE_COLOR = "darkgrey";
// Arrows are drawn ON their band, so they can't take the band's colour or only the
// white halo would show. Black plus that halo reads on every band in the palette.
const ARROW_COLOR = "black";
// A reversible lane is double-headed like both_ways; the dash is what tells them
// apart. Screen pixels, like every other width here.
const REVERSIBLE_DASH = [5, 4];
// A time-conditional restriction dashes the BAND: the casing showing through the
// gaps says "this colour holds only sometimes", matching how the reversible dash
// already means "not always" for direction. Longer marks than REVERSIBLE_DASH so
// the two stay tellable apart; when a lane is both, the restriction dash wins —
// the restriction is what this layer exists to show.
const CONDITIONAL_DASH = [9, 5];
// Hover and click keep the bands and add a halo underneath instead of replacing the
// stroke with one flat colour the way the per-way styles do — the per-lane detail is
// the whole point of this layer, so hiding it on select would be backwards.
const HIGHLIGHT_EXTRA_PX = 4;
// Butt caps, not OL's default round. A round cap overhangs the line end by half the
// stroke width, so where two ways meet at a node their casings bulge past each other
// into a dark blob — and the wider the ribbon, the bigger the blob, which is why it was
// worst exactly where the lane count changed. Butt caps end the ribbon flat on the node,
// so neighbours with different lane counts abut as a clean step. `lineJoin: "round"`
// stays, since that only affects interior bends.
const LANE_CAP = "butt";
// How far the casing runs past each end, in screen pixels. Butt caps alone left the
// ribbons stopping short of each other at a junction: the shared node sits INSIDE the
// crossing ribbon, so the stretch between node and the crossing ribbon's far edge went
// unpainted. Overshooting the casing fills it and adjacent casings overlap into a joint.
// Constant, unlike the round cap's half-the-stroke-width, which is what made the old
// blobs grow with the lane count. It also closes the seam between two step sections.
const CASING_OVERSHOOT_PX = 5;
// The casing must be under EVERY band, including bands of the neighbouring section drawn
// later in the same style array — otherwise the overshoot paints over the previous
// section's band ends. Separate zIndexes rather than array order get that right.
const Z_LANE_CASING = Z_RESTRICTION;
const Z_LANE_BAND = Z_RESTRICTION + 1;

// bus only ==> maroon, taxi+bus ==> blue, taxi only ==> yellow, hgv banned ==>
// purple, otherwise restricted ==> darkgrey, hov ==> orange, else open. Access beats
// hov: a bus lane that is also HOV is first of all a bus lane.
//
// `slot` values are per-lane scalars ("no", or blank for allowed), so these are
// equality tests rather than the `.includes("no")` the per-way styles used to need.
function classifyLane(slot) {
  const hgvBanned = slot.rm_vehicleAccessHgv === "no";
  const carBanned = slot.rm_vehicleAccessMotorcar === "no";
  const busBanned = slot.rm_vehicleAccessBus === "no";
  const taxiBanned = slot.rm_vehicleAccessTaxi === "no";
  if (hgvBanned && carBanned && taxiBanned && !busBanned) return "maroon";
  if (hgvBanned && carBanned && !busBanned && !taxiBanned) return "blue";
  if (hgvBanned && carBanned && busBanned && !taxiBanned) return "yellow";
  if (hgvBanned && !carBanned && !busBanned && !taxiBanned) return "purple";
  // Fully closed, and equally every combination the palette above doesn't name
  // (cars banned but hgv allowed, say). Painting those as open would state the
  // opposite of the data; dark grey says "restricted — read the info panel".
  if (hgvBanned || carBanned || busBanned || taxiBanned)
    return CLOSED_LANE_COLOR;
  // The new-spec conditional form only ever writes `yes`, but the unconditional
  // OSM-normalized value is `designated` (with `yes` meaning "HOV also allowed" on
  // some ways) — both mark the HOV lane itself.
  if (HOV_LANE_VALUES.includes(slot.rm_hovLanes)) return "orange";
  // A time-conditional HOV lane is still an HOV lane — same orange; the dash from
  // laneConditional() is what says "only during the posted window".
  const conditional = splitConditionalValue(slot.rm_hovLanesConditional);
  if (conditional && HOV_LANE_VALUES.includes(conditional.value))
    return "orange";
  return OPEN_LANE_COLOR;
}

const HOV_LANE_VALUES = ["yes", "designated"];

// The condition text of a slot's conditional restriction, or "" — parallel to
// classifyLane so classifyLanes stays plain JSON for styleBySection's merge.
function laneConditional(slot) {
  return (
    splitConditionalValue(slot.rm_hovLanesConditional)?.condition ??
    splitConditionalValue(slot.rm_hovMinimumLanesConditional)?.condition ??
    ""
  );
}

// With no per-lane information there is nothing to band, but a whole-way `hov` is
// still worth colouring. It is raw OSM off the basemap that the normalization could
// not expand per-lane — it had no lane count to size the list to — so it never becomes
// `hov:lanes` and nothing else here would surface it. Orange, matching the HOV layer.
//
// Deliberately NOT delegating to sdLaneRestriction's per-way classifier for the rest:
// this branch is only reached when every per-lane list is empty, which are exactly that
// classifier's inputs, so it could only ever return the hairline — except on
// styleBySection's zero-length fallback, where it receives RAW tags and its
// `.includes("no")` would paint a whole way off one restricted stretch. That is the bug
// splitLaneList's chain guard exists to prevent.
const HOV_PRESENT = ["designated", "yes", "lane"];

function classifyNoLanes(tags, plain) {
  if (HOV_PRESENT.includes(tags.hov)) {
    return { color: "orange", width: 8, zIndex: Z_RESTRICTION };
  }
  // `plain` carries the road class's own zIndex, so an unremarkable major sits at the
  // major-road level rather than being promoted to the restriction level.
  return plain;
}

// Plain JSON only — styleBySection merges adjacent sections by
// JSON.stringify(descriptor), and a geometry or a function in here would break that
// silently.
function classifyLanes(tags, plain) {
  const count = laneCount(tags);
  if (count === 0)
    return { laneCount: 0, perWay: classifyNoLanes(tags, plain) };
  const slots = laneSlots(tags, count);
  return {
    laneCount: count,
    colors: slots.map(classifyLane),
    directions: slots.map((slot) => slot.rm_lanesDirection),
    conditionals: slots.map(laneConditional),
  };
}

// Spec domain -> what the shared arrow helper understands. `reversible` has no arrow
// of its own: it draws double-headed like `both_ways` and is told apart by the dash.
function arrowDirection(direction) {
  if (direction === "both_ways" || direction === "reversible") return "both";
  if (direction === "forward" || direction === "backward") return direction;
  return "none";
}

// One arrow per lane. The HD helper puts two on a long line, which here would be
// multiplied by the lane count and again by the section count — a cluster instead of
// an annotation.
//
// Arrows are STAGGERED along the line, lane i at (i+1)/(n+1), rather than all sitting
// at the midpoint. An arrow is ~9px long plus a 3.5px halo against a 1.5-2.6px band,
// so it necessarily spills onto its neighbours; putting every lane's arrow at the
// same fraction stacked that spill into an unreadable blob across the whole ribbon.
// Spread out, each arrow overlaps only plain band and stays legible.
function laneArrowStyles(coords, direction, resolution, lane, laneTotal) {
  const specs = laneDirectionArrowSpecs(arrowDirection(direction));
  if (!specs.length) return [];
  if (!resolution || resolution > ARROW_HIDE_RESOLUTION) return [];
  const { point, rotation } = sampleAlong(coords, (lane + 1) / (laneTotal + 1));
  return specs.flatMap(({ headingOffset }) =>
    buildArrowStyles(point, rotation, headingOffset, ARROW_COLOR, resolution),
  );
}

// One stroke, no bands: either the lane count is unknown (banding it would invent
// lanes) or there is no LineString to offset. `perWay` carries whatever the whole-way
// fallback classified, so a restricted way still gets its colour instead of a hairline.
function plainStyles(descriptor, geometry, click, hover) {
  const {
    color = "black",
    width = 2,
    zIndex = Z_RESTRICTION,
  } = descriptor.perWay || {};
  if (click) {
    return [
      new Style({
        geometry,
        zIndex,
        stroke: new Stroke({ color, width: width + 4 }),
      }),
      new Style({
        geometry,
        zIndex,
        stroke: new Stroke({ color: "orange", width: 4 }),
      }),
    ];
  }
  if (hover) {
    return [
      new Style({
        geometry,
        zIndex,
        stroke: new Stroke({ color: "white", width: width + 6 }),
      }),
      new Style({
        geometry,
        zIndex,
        stroke: new Stroke({ color, width: width + 2 }),
      }),
    ];
  }
  return [
    new Style({ geometry, zIndex, stroke: new Stroke({ color, width }) }),
  ];
}

function toLaneStyles(descriptor, geometry, feature, resolution, click, hover) {
  // `geometry === undefined` is styleBySection's "no step tag, use the feature's own
  // geometry" signal. OL honours that on a Style, but the lane offsets need actual
  // coordinates, so fall back explicitly.
  const line = geometry ?? feature.getGeometry();
  const coords =
    line && line.getType() === "LineString" ? line.getCoordinates() : undefined;
  if (!descriptor.laneCount || !coords || coords.length < 2) {
    return plainStyles(descriptor, geometry, click, hover);
  }

  const { totalW, band, offsets } = bandGeometry(descriptor.laneCount);
  const styles = [];
  if (click || hover) {
    styles.push(
      new Style({
        geometry,
        zIndex: Z_LANE_CASING,
        stroke: new Stroke({
          color: click ? "orange" : "white",
          width: totalW + HIGHLIGHT_EXTRA_PX,
          lineCap: LANE_CAP,
        }),
      }),
    );
  }
  styles.push(
    new Style({
      geometry: new LineString(
        extendEnds(coords, CASING_OVERSHOOT_PX * (resolution || 1)),
      ),
      zIndex: Z_LANE_CASING,
      stroke: new Stroke({
        color: SEPARATOR_COLOR,
        width: totalW,
        lineCap: LANE_CAP,
      }),
    }),
  );
  descriptor.colors.forEach((color, lane) => {
    const direction = descriptor.directions[lane];
    const conditional = descriptor.conditionals?.[lane];
    // Band widths are already screen pixels, which is what OL strokes take; the
    // offsets move geometry, so those go through the resolution.
    const laneCoords = offsetCoordinates(
      coords,
      offsets[lane] * (resolution || 1),
    );
    styles.push(
      new Style({
        geometry: new LineString(laneCoords),
        zIndex: Z_LANE_BAND,
        stroke: new Stroke({
          color,
          width: band,
          lineCap: LANE_CAP,
          lineDash: conditional
            ? CONDITIONAL_DASH
            : direction === "reversible"
              ? REVERSIBLE_DASH
              : undefined,
        }),
      }),
      ...laneArrowStyles(
        laneCoords,
        direction,
        resolution,
        lane,
        descriptor.colors.length,
      ),
    );
  });
  return styles;
}

function getLanesStyle(feature, resolution, click = false, hover = false) {
  // Ways with no `highway=` are dropped: the basemap never called them roads.
  const cls = roadClass(feature);
  if (cls === ROAD_CLASS_UNTAGGED) return [];
  const plain = roadClassStroke(cls);
  // `service` is the one minor kept unconditionally, and as its plain grey line rather
  // than banded — a driveway is context, not data.
  if (cls === ROAD_CLASS_SERVICE) {
    return plainStyles({ perWay: plain }, undefined, click, hover);
  }
  const tags = extractTags(feature);
  // Other minors are dropped only when they have nothing per-lane to say. Hiding them
  // is about clutter — a footway with no lane data is noise on a lane layer — but a
  // track that does carry vehicle_access or hov IS the data this layer exists for, so
  // it gets banded like any major rather than silently vanishing.
  if (cls === ROAD_CLASS_MINOR && laneCount(tags) === 0) return [];
  return styleBySection(
    feature,
    tags,
    (tags) => classifyLanes(tags, plain),
    (descriptor, geometry) =>
      toLaneStyles(descriptor, geometry, feature, resolution, click, hover),
  );
}

export function getLanesStyleNormal(feature, resolution) {
  return getLanesStyle(feature, resolution);
}

export function getLanesStyleClick(feature, resolution) {
  return getLanesStyle(feature, resolution, true, false);
}

export function getLanesStyleHover(feature, resolution) {
  return getLanesStyle(feature, resolution, false, true);
}
